import type { QueryClient } from "@tanstack/react-query";

import type {
  Command,
  CommandBatch,
  CommandStatus,
  Device,
  LiveEvent,
  Page,
  TelemetryPoint,
  TelemetrySeries,
} from "./types";

// The keys under which the dashboard caches what it fetched. Live events patch the
// cached data in place, so the screen changes the moment something happens instead of
// at the next refetch.
export const keys = {
  devices: ["devices"] as const,
  device: (id: string) => ["device", id] as const,
  fleetSummary: ["fleet-summary"] as const,
  settings: ["fleet-settings"] as const,
  groups: ["groups"] as const,
  alerts: ["alerts"] as const,
  alertSummary: ["alert-summary"] as const,
  commands: ["commands"] as const,
  deviceCommands: (deviceId: string) => ["commands", "device", deviceId] as const,
  batches: ["batches"] as const,
  batch: (id: string) => ["batch", id] as const,
  telemetry: (deviceId: string) => ["telemetry", deviceId] as const,
  liveSessions: (deviceId: string) => ["live-sessions", deviceId] as const,
  users: ["users"] as const,
  enrollmentTokens: ["enrollment-tokens"] as const,
  files: ["files"] as const,
  audit: ["audit"] as const,
};

/** How many points a live chart keeps before it drops the oldest. */
const MAX_LIVE_POINTS = 1000;

function patchDevice(client: QueryClient, id: string, change: (device: Device) => Device): void {
  client.setQueryData<Device[]>(keys.devices, (devices) =>
    devices?.map((device) => (device.id === id ? change(device) : device)),
  );
  client.setQueryData<Device>(keys.device(id), (device) => (device ? change(device) : device));
}

function later(a: string | null | undefined, b: string): string {
  return a && Date.parse(a) > Date.parse(b) ? a : b;
}

function countStatuses(commands: Command[]): Partial<Record<CommandStatus, number>> {
  const counts: Partial<Record<CommandStatus, number>> = {};
  for (const command of commands) counts[command.status] = (counts[command.status] ?? 0) + 1;
  return counts;
}

/** Commands that hold a device's screen or microphone while they run. */
const LIVE_TYPES = new Set(["screen_view", "screen_stream", "audio_capture"]);

function applyCommand(client: QueryClient, command: Command): void {
  // What is live on the device may have changed: a viewing started, or ended.
  if (LIVE_TYPES.has(command.type)) {
    void client.invalidateQueries({ queryKey: keys.liveSessions(command.device_id) });
  }

  // Lists of commands (a device's history): replace it where it is shown; a command
  // nobody has seen yet means the first page changed, so fetch that again.
  let known = false;
  client.setQueriesData<Page<Command>>({ queryKey: keys.commands }, (page) => {
    if (!page?.items.some((item) => item.id === command.id)) return page;
    known = true;
    return {
      ...page,
      items: page.items.map((item) => (item.id === command.id ? command : item)),
    };
  });
  if (!known) void client.invalidateQueries({ queryKey: keys.deviceCommands(command.device_id) });

  client.setQueryData<CommandBatch>(keys.batch(command.batch_id), (batch) => {
    if (!batch) return batch;
    const commands = batch.commands.some((item) => item.id === command.id)
      ? batch.commands.map((item) => (item.id === command.id ? command : item))
      : [...batch.commands, command];
    return { ...batch, commands, total: commands.length, status_counts: countStatuses(commands) };
  });
}

/** Applies one event from the server to the cached data. */
export function applyEvent(client: QueryClient, event: LiveEvent): void {
  switch (event.event) {
    case "device.online":
      patchDevice(client, event.data.device_id, (device) => ({
        ...device,
        is_online: true,
        last_connected_at: event.at,
        last_seen_at: later(device.last_seen_at, event.at),
        last_ip: event.data.ip ?? device.last_ip,
      }));
      void client.invalidateQueries({ queryKey: keys.fleetSummary });
      break;

    case "device.offline":
      patchDevice(client, event.data.device_id, (device) => ({
        ...device,
        is_online: false,
        last_disconnected_at: event.at,
      }));
      void client.invalidateQueries({ queryKey: keys.fleetSummary });
      break;

    case "device.updated": {
      // The server sends the whole device when it can (a new position, for one);
      // otherwise what changed is unknown and the device is fetched again.
      const { device_id: deviceId, device: updated } = event.data;
      if (updated) {
        patchDevice(client, deviceId, () => updated);
      } else {
        void client.invalidateQueries({ queryKey: keys.device(deviceId) });
        void client.invalidateQueries({ queryKey: keys.devices });
      }
      break;
    }

    case "device.telemetry": {
      const { device_id: deviceId, sample } = event.data;
      const recordedAt = sample.recorded_at ?? event.at;
      patchDevice(client, deviceId, (device) => {
        // Samples queued while the device was offline arrive late: they must not
        // replace a newer reading.
        const newer =
          !device.latest_telemetry_at ||
          Date.parse(recordedAt) >= Date.parse(device.latest_telemetry_at);
        // A sample with coordinates is also the device's newest known position.
        const located = typeof sample.latitude === "number" && typeof sample.longitude === "number";
        const newerPosition =
          located && (!device.location_at || Date.parse(recordedAt) >= Date.parse(device.location_at));
        return {
          ...device,
          last_seen_at: later(device.last_seen_at, event.at),
          latest_telemetry: newer ? sample : device.latest_telemetry,
          latest_telemetry_at: newer ? recordedAt : device.latest_telemetry_at,
          ...(newerPosition
            ? {
                location_latitude: sample.latitude ?? null,
                location_longitude: sample.longitude ?? null,
                location_accuracy_m: sample.location_accuracy_m ?? null,
                location_at: recordedAt,
              }
            : {}),
        };
      });
      // Charts that are open grow by the new reading, unless it belongs to an
      // interval that is already drawn (the next refetch averages it in).
      client.setQueriesData<TelemetrySeries>({ queryKey: keys.telemetry(deviceId) }, (series) => {
        if (!series) return series;
        const time = Date.parse(recordedAt);
        const last = series.points.at(-1);
        if (last && time < Date.parse(last.time) + series.interval_seconds * 1000) return series;
        const point: TelemetryPoint = {
          time: recordedAt,
          samples: 1,
          battery_percent: sample.battery_percent ?? null,
          temperature_c: sample.temperature_c ?? null,
          cpu_load_percent: sample.cpu_load_percent ?? null,
          ram_used_mb: sample.ram_used_mb ?? null,
          ram_total_mb: sample.ram_total_mb ?? null,
          storage_used_mb: sample.storage_used_mb ?? null,
          storage_total_mb: sample.storage_total_mb ?? null,
        };
        return { ...series, points: [...series.points, point].slice(-MAX_LIVE_POINTS) };
      });
      break;
    }

    case "command.updated":
      applyCommand(client, event.data.command);
      void client.invalidateQueries({ queryKey: keys.batches });
      void client.invalidateQueries({ queryKey: keys.fleetSummary });
      break;

    case "command.batch_dispatched":
      void client.invalidateQueries({ queryKey: keys.batches });
      void client.invalidateQueries({ queryKey: keys.fleetSummary });
      break;

    case "alert.opened":
    case "alert.updated":
      void client.invalidateQueries({ queryKey: keys.alerts });
      void client.invalidateQueries({ queryKey: keys.alertSummary });
      void client.invalidateQueries({ queryKey: keys.fleetSummary });
      break;
  }
}

/** What happened while the connection was down is unknown: fetch everything again. */
export function resynchronize(client: QueryClient): void {
  void client.invalidateQueries();
}
